/* ---- <offer-popup> ---- */
// Opens the Offer popup's <ft-modal> (theme.js: modal layer, enter/exit animation, Escape, backdrop) once per visitor:
// the Delay counts from their first interaction, unless they closed it with its close button within "Show again
// after" days (a cookie; 0 days = once per visit, in sessionStorage). Closing it from the backdrop doesn't count.
// Test mode skips that check so the merchant can see it on every load. In the theme editor it opens while its section
// or one of its blocks is selected, and closes when deselected.
(() => {
  'use strict';
  if (customElements.get('offer-popup')) return;

  const DESIGN = !!window.Shopify?.designMode;
  let selected = null; // editor: id of the selected Offer popup section, so a re-rendered one reopens

  // Like the theme's carousel autoplay and marquees: nothing covers the page while it is still loading. The delay
  // starts with the visitor's first move, touch, wheel or key press, which for a person is all but immediate. Not on
  // 'scroll': the page fires that by itself while loading (scroll restoration, test tools), which opened it under Lighthouse.
  const FIRST_INPUT = new Promise((resolve) => {
    const events = ['pointermove', 'pointerdown', 'touchstart', 'keydown', 'wheel'];
    const done = () => { events.forEach((e) => window.removeEventListener(e, done, true)); resolve(); };
    events.forEach((e) => window.addEventListener(e, done, { capture: true, passive: true }));
  });

  const readCookie = (name) => document.cookie.split('; ').some((c) => c.startsWith(`${name}=`));

  // The section's stylesheet loads in the background (media="print" until loaded): wait for it before showing.
  const styled = () => {
    const link = document.querySelector('link[href*="section-offer-popup"]');
    if (!link || link.media !== 'print') return Promise.resolve();
    return new Promise((resolve) => {
      link.addEventListener('load', resolve, { once: true });
      setTimeout(resolve, 3000);
    });
  };

  class OfferPopup extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.modal = this.querySelector('ft-modal');
      if (!this.modal) return;
      this.id_ = this.dataset.sectionId;
      this.key = `offer_popup_${this.id_}`;

      // Remembered (not shown again for "Show again after" days) only when the visitor chooses to: the close button,
      // Escape (the close button's keyboard twin) or a link inside. A click on the backdrop just closes it for now,
      // so it shows again on the next page load.
      this.modal.addEventListener('click', (e) => {
        if (e.target.closest('.offer-popup__close, a[href]')) this.remember();
      });
      this.modal.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.remember(); });

      if (DESIGN) {
        this.onSelect = (e) => {
          if (e.detail?.sectionId !== this.id_) return;
          selected = this.id_;
          this.open();
        };
        this.onDeselect = (e) => {
          if (e.detail?.sectionId !== this.id_) return;
          if (selected === this.id_) selected = null;
          this.modal.close?.();
        };
        document.addEventListener('shopify:section:select', this.onSelect);
        document.addEventListener('shopify:block:select', this.onSelect);
        document.addEventListener('shopify:section:deselect', this.onDeselect);
        if (selected === this.id_) this.open();
        return;
      }

      if (this.dataset.mode !== 'test' && this.seen()) return;
      const delay = Math.max(0, parseFloat(this.dataset.delay) || 0) * 1000;
      FIRST_INPUT.then(() => { if (this.isConnected) this.timer = setTimeout(() => this.open(), delay); });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.onSelect) {
        document.removeEventListener('shopify:section:select', this.onSelect);
        document.removeEventListener('shopify:block:select', this.onSelect);
        document.removeEventListener('shopify:section:deselect', this.onDeselect);
      }
      this.bound = false;
    }

    async open() {
      await customElements.whenDefined('ft-modal');
      await styled();
      if (!this.isConnected || this.modal.hasAttribute('open')) return;
      // Something else is open (cart drawer, quick view): don't stack the offer on top of it.
      if (!DESIGN && window.theme?.modal?.top) return;
      this.modal.open();
    }

    seen() {
      if (readCookie(this.key)) return true;
      try { return !!sessionStorage.getItem(this.key); } catch { return false; }
    }

    remember() {
      if (DESIGN || this.dataset.mode === 'test') return;
      const days = parseInt(this.dataset.expireDays, 10) || 0;
      if (days > 0) {
        document.cookie = `${this.key}=1; max-age=${days * 86400}; path=/; SameSite=Lax`;
      } else {
        try { sessionStorage.setItem(this.key, '1'); } catch {}
      }
    }
  }

  customElements.define('offer-popup', OfferPopup);
})();
